import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Text } from '@components/atoms/ui-kit/text.tsx';
import {
  isRetryableVoiceError,
  voiceErrorKey
} from '@utils/editors/voice-profile-errors.ts';

interface Props {
  error: unknown;
  /** Offered only for conditions where retrying can actually succeed. */
  onRetry?: () => void;
  retryLabel?: string;
}

/** One actionable explanation for a failed voice-profile request.
 *
 * `role="alert"` so a screen reader announces it when the request fails
 * without the user having to go looking for it. The message is the specific
 * cause — short selection, missing media, sample limit — because that is the
 * only part the user can act on. */
export const VoiceErrorMessage = ({ error, onRetry, retryLabel }: Props) => {
  const { t } = useTranslation();
  if (!error) return null;

  const canRetry = !!onRetry && isRetryableVoiceError(error);

  return (
    <div role="alert" className="flex items-start gap-2">
      <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
      <div className="space-y-1">
        <Text as="p" size="sm" tone="danger">
          {t(voiceErrorKey(error))}
        </Text>
        {canRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="text-sm font-semibold underline cursor-pointer"
          >
            {retryLabel ?? t('retry')}
          </button>
        )}
      </div>
    </div>
  );
};
